import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { selectAmountFromCategory } from 'src/utils/contracts';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  MenuItem,
  Typography,
  Slide,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DropdownMenu from 'src/components/DropdownMenu';
import {
  LOAD_CLIENT_CONTRACTS,
  loadClientContracts
} from 'src/store/actions/clientActions';
import { selectClientContractList } from 'src/store/selectors/clientSelectors';
import {
  selectUpfrontRent,
  selectNextRent
} from 'src/store/selectors/contractSelectors';
import ContractStatus from 'src/utils/constants/contracts';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import {
  PaymentFormList,
  TransactionsCategory,
  TransactionsType
} from 'src/utils/constants/transactions';
import { PaymentMethod } from 'src/utils/constants/cfdi';
import { getPaymentMethod, Parameter } from 'src/utils/constants/parameters';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { CREATE_MANUAL_TRANSACTION } from 'src/store/actions/transactionActions';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import { selectIndividualParameter } from 'src/store/selectors/parameterSelector';
import { loadParameter } from 'src/store/actions/parameterActions';
import {
  loadUpfrontRentNextPeriod,
  loadNextRentPeriod
} from 'src/store/actions/contractActions';
import { getAmountWithIva, SnackBarVariants } from 'src/utils/constants/common';
import {
  getContractUniqueChecks,
  getManualChargeConceptPreview,
  getManualChargeSurchargePreview
} from 'src/services/transactionsService';
import { showMessage } from 'src/store/actions/snackBarActions';
import TransactionDialogHeader from '../TransactionDialogHeader';
import FieldLabel from '../FieldLabel';
import makeInitialState from './initialState';
import validationSchema from './schema';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const categoryOptions = [
  TransactionsCategory.RENT,
  TransactionsCategory.UPFRONT_RENT,
  TransactionsCategory.RESIDUAL,
  TransactionsCategory.INITIAL_PAYMENT,
  TransactionsCategory.SURCHARGE,
  TransactionsCategory.COMMISSION
];

const invalidFieldSx = {
  '& .MuiFormLabel-root.Mui-error': { color: 'secondary.main' },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: 'secondary.main'
  },
  '& .MuiFormHelperText-root.Mui-error': { color: 'secondary.main' }
};

const GenerateManualChargeDialog = ({
  open,
  handleClose,
  submit,
  clientId
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const contracts = useSelector(selectClientContractList);
  const upFrontRent = useSelector(selectUpfrontRent);
  const [upFrontError, setUpFrontError] = useState(null);
  const nextRent = useSelector(selectNextRent);
  const [rentError, setRentError] = useState(null);
  const ivaParameter = useSelector(selectIndividualParameter) || 16;
  const loading = useSelector(
    isLoadingSelector([CREATE_MANUAL_TRANSACTION.action])
  );
  const initialState = useMemo(() => makeInitialState(), []);
  const resetRef = useRef();
  const latestContractIdRef = useRef(null);
  const { t } = useTranslation();
  const [selectedContract, setSelectedContract] = useState(null);
  const [hasInitialPayment, setHasInitialPayment] = useState(false);
  const [hasResidual, setHasResidual] = useState(false);
  const [hasRentForCurrentPeriod, setHasRentForCurrentPeriod] = useState(
    false
  );
  const [surchargeInfo, setSurchargeInfo] = useState({
    amount: 0,
    hasOverdue: false
  });
  const [conceptTouchedByUser, setConceptTouchedByUser] = useState(false);
  const [isFetchingConcept, setIsFetchingConcept] = useState(false);
  const isLoadingContracts = useSelector(
    isLoadingSelector([LOAD_CLIENT_CONTRACTS.action])
  );

  useEffect(() => {
    if (open) {
      dispatch(
        loadClientContracts(
          { clientId, status: ContractStatus.IN_PROGRESS },
          false
        )
      );
      dispatch(loadParameter(Parameter.IVA));
    }
  }, [dispatch, id, open]);

  useEffect(() => {
    if (upFrontRent) {
      if (Number(upFrontRent.period) > Number(upFrontRent.totalPeriods)) {
        setUpFrontError(true);
      }
    }
  }, [upFrontRent]);

  useEffect(() => {
    if (nextRent) {
      if (Number(nextRent.period) > Number(nextRent.totalPeriods)) {
        setRentError(true);
      } else {
        setRentError(false);
      }
    }
  }, [nextRent]);

  const fetchUpfrontRentDate = useCallback(() => {
    dispatch(loadUpfrontRentNextPeriod({ id: selectedContract.id }));
  }, [selectedContract]);

  const fetchNextRentDate = useCallback(() => {
    if (selectedContract) {
      dispatch(loadNextRentPeriod({ id: selectedContract.id }));
    }
  }, [selectedContract, dispatch]);

  const fetchConceptPreview = useCallback(
    async (category, contract, setFieldValue) => {
      if (conceptTouchedByUser || !contract) return;
      setIsFetchingConcept(true);
      try {
        const { concept } = await getManualChargeConceptPreview({
          contractId: contract.id,
          category
        });
        if (concept) {
          setFieldValue('comment', concept);
        }
      } catch {
        // preview is a convenience only — leave the field as-is on failure
      } finally {
        setIsFetchingConcept(false);
      }
    },
    [conceptTouchedByUser]
  );

  const handleCategoryChange = useCallback(
    (category, contract) => {
      if (category === TransactionsCategory.UPFRONT_RENT && contract) {
        fetchUpfrontRentDate(contract.id);
      }
      if (category === TransactionsCategory.RENT && contract) {
        fetchNextRentDate();
      }
      setRentError(null);
    },
    [fetchUpfrontRentDate, fetchNextRentDate]
  );

  const handleSubmitTransaction = useCallback(
    values => {
      // Don't reset here — the form must stay visible with its values while
      // the request is in flight (submit button shows the loading state).
      // The dialog-close effect below resets the form once it actually closes.
      submit(
        {
          ...values,
          type: TransactionsType.CHARGE,
          amount: getAmountWithIva(values.amount, ivaParameter),
          equipmentId: Number(values.contract.equipment.id),
          contractId: values.contract.id,
          paymentForm: values.paymentForm
        },
        handleClose
      );
    },
    [submit, handleClose]
  );

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  useEffect(() => {
    if (!open) {
      setHasInitialPayment(false);
      setHasResidual(false);
      setHasRentForCurrentPeriod(false);
      setSurchargeInfo({ amount: 0, hasOverdue: false });
      setConceptTouchedByUser(false);
      setIsFetchingConcept(false);
    }
  }, [open]);

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { width: 680 } }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <TransactionDialogHeader
        title={t('COMMON.REGISTER_MANUAL_CHARGE')}
        subtitle={t('TRANSACTION.CREATE_CHARGE_SUBTITLE')}
        onClose={handleClose}
      />
      <DialogContent style={{ marginBottom: '16px' }}>
        <Formik
          initialValues={initialState}
          validationSchema={validationSchema}
          onSubmit={handleSubmitTransaction}
          enableReinitialize
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            resetForm,
            setFieldValue
          }) => {
            resetRef.current = () => resetForm();
            return (
              <form onSubmit={handleSubmit}>
                <FieldLabel label={t('COMMON.CONTRACT')}>
                  <DropdownMenu
                    list={contracts.map(contract => ({
                      key: contract,
                      name: `${t('COMMON.CONTRACT')} ${contract.id} - ${t(
                        'CONTRACT_VIEW.RENT_WITH_IVA'
                      )} $${formatAmountWithDecimals(contract.rentIvaIncluded)}`
                    }))}
                    value={values.contract}
                    setValue={async value => {
                      latestContractIdRef.current = value.id;
                      setFieldValue('contract', value);
                      setSelectedContract(value);
                      setFieldValue('category', null);
                      setUpFrontError(null);
                      setHasInitialPayment(false);
                      setHasResidual(false);
                      setHasRentForCurrentPeriod(false);
                      setSurchargeInfo({ amount: 0, hasOverdue: false });

                      // Fire both requests concurrently — await each in its
                      // own try/catch below so their independent error
                      // handling stays unchanged.
                      const uniqueChecksRequest = getContractUniqueChecks(
                        value.id
                      );
                      const surchargePreviewRequest = getManualChargeSurchargePreview(
                        { contractId: value.id }
                      );

                      try {
                        const checks = await uniqueChecksRequest;
                        // A newer contract selection may have already
                        // superseded this one while we were awaiting —
                        // discard this stale response.
                        if (latestContractIdRef.current !== value.id) return;
                        setHasInitialPayment(checks.hasInitialPayment);
                        setHasResidual(checks.hasResidual);
                        setHasRentForCurrentPeriod(
                          checks.hasRentForCurrentPeriod
                        );
                      } catch {
                        // fail silently — backend guard is the safety net
                      }

                      try {
                        const preview = await surchargePreviewRequest;
                        if (latestContractIdRef.current !== value.id) return;
                        setSurchargeInfo(
                          preview ?? { amount: 0, hasOverdue: false }
                        );
                      } catch {
                        // Fail closed (hasOverdue stays false, so the
                        // category option stays disabled) but tell the user
                        // why, since this isn't a soft default like the
                        // unique-checks call above — it actively blocks
                        // the option.
                        if (latestContractIdRef.current !== value.id) return;
                        dispatch(
                          showMessage({
                            message: 'SURCHARGE_PREVIEW_FAILED',
                            variant: SnackBarVariants.ERROR
                          })
                        );
                      }
                    }}
                    defaultValue={values.contract}
                    error={Boolean(touched.contract && errors.contract)}
                    errorText={touched.contract && t(errors.contract)}
                    disabled={isLoadingContracts}
                    isLoading={isLoadingContracts}
                    sx={invalidFieldSx}
                  />
                </FieldLabel>

                <FieldLabel label={t('COMMON.CATEGORY')}>
                <TextField
                  select
                  fullWidth
                  margin="none"
                  variant="outlined"
                  sx={invalidFieldSx}
                  value={values.category || ''}
                  onChange={e => {
                    const value = e.target.value;
                    setFieldValue('category', value);
                    const amount =
                      value === TransactionsCategory.SURCHARGE
                        ? surchargeInfo.amount
                        : selectAmountFromCategory(value, values.contract);
                    setFieldValue('amount', formatAmountWithDecimals(amount, 2));
                    setUpFrontError(null);
                    handleCategoryChange(value, values.contract);
                    const isPueCategory = [
                      TransactionsCategory.COMMISSION,
                      TransactionsCategory.RESIDUAL,
                      TransactionsCategory.UPFRONT_RENT
                    ].includes(value);
                    const newPaymentMethod = isPueCategory
                      ? PaymentMethod.PUE
                      : PaymentMethod.PPD;
                    setFieldValue('paymentMethod', newPaymentMethod);
                    if (!isPueCategory) {
                      setFieldValue('paymentForm', '');
                    }
                    fetchConceptPreview(value, values.contract, setFieldValue);
                  }}
                  error={Boolean(touched.category && errors.category)}
                  helperText={touched.category && t(errors.category)}
                  disabled={!values.contract}
                  SelectProps={{
                    renderValue: value =>
                      value ? t(`TRANSACTION.CATEGORY.${value}`) : '',
                    MenuProps: {
                      PaperProps: { style: { maxHeight: 300 } }
                    }
                  }}
                >
                  {categoryOptions
                    .filter(category => {
                      const isUpfrontRentContract = Boolean(
                        values.contract?.isFirstUpfrontRentModality
                      );
                      if (category === TransactionsCategory.INITIAL_PAYMENT) {
                        return !isUpfrontRentContract && !hasInitialPayment;
                      }
                      return true;
                    })
                    .map(category => {
                      const isResidualDisabled =
                        category === TransactionsCategory.RESIDUAL &&
                        hasResidual;
                      const isRentDisabled =
                        category === TransactionsCategory.RENT &&
                        hasRentForCurrentPeriod;
                      const isSurchargeDisabled =
                        category === TransactionsCategory.SURCHARGE &&
                        !surchargeInfo.hasOverdue;
                      const isDisabled =
                        isResidualDisabled ||
                        isRentDisabled ||
                        isSurchargeDisabled;

                      let disabledReason = null;
                      if (isResidualDisabled) {
                        disabledReason = t(
                          'ERRORS.RESIDUAL_ALREADY_EXISTS_FOR_CONTRACT'
                        );
                      } else if (isRentDisabled) {
                        disabledReason = t(
                          'ERRORS.RENT_ALREADY_EXISTS_FOR_PERIOD'
                        );
                      } else if (isSurchargeDisabled) {
                        disabledReason = t(
                          'ERRORS.NO_OVERDUE_INVOICES_FOR_SURCHARGE'
                        );
                      }

                      return (
                        <MenuItem
                          key={category}
                          value={category}
                          disabled={isDisabled}
                        >
                          <span>
                            <span style={{ display: 'block' }}>
                              {t(`TRANSACTION.CATEGORY.${category}`)}
                            </span>
                            {disabledReason && (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                style={{ display: 'block' }}
                              >
                                {disabledReason}
                              </Typography>
                            )}
                          </span>
                        </MenuItem>
                      );
                    })}
                </TextField>
                </FieldLabel>

                {upFrontError && (
                  <Typography
                    variant="body2"
                    color="secondary.main"
                    style={{ marginTop: '8px' }}
                  >
                    {t('SCHEMA_ERRORS.UPFRONT_RENT_LIMIT')}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FieldLabel label={t('COMMON.AMOUNT')}>
                      <TextField
                        InputProps={{
                          autoComplete: 'off',
                          inputComponent: MoneyMask
                        }}
                        sx={invalidFieldSx}
                        error={Boolean(touched.amount && errors.amount)}
                        fullWidth
                        helperText={touched.amount && t(errors.amount)}
                        margin="none"
                        name="amount"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.amount}
                        variant="outlined"
                        disabled={
                          values.category ===
                            TransactionsCategory.UPFRONT_RENT ||
                          !values.contract
                        }
                      />
                    </FieldLabel>
                  </Grid>
                  <Grid item xs={4}>
                    <FieldLabel label={t('COMMON.IVA')} disabled>
                      <TextField
                        InputProps={{
                          readOnly: true
                        }}
                        fullWidth
                        margin="none"
                        name="iva"
                        value={`${formatAmountWithDecimals(ivaParameter, 1)}%`}
                        variant="outlined"
                        disabled
                      />
                    </FieldLabel>
                  </Grid>
                  <Grid item xs={4}>
                    <FieldLabel label={t('COMMON.AMOUNT_WITH_IVA')} disabled>
                      <TextField
                        InputProps={{
                          autoComplete: 'off',
                          inputComponent: MoneyMask,
                          readOnly: true
                        }}
                        fullWidth
                        margin="none"
                        name="amountWithIva"
                        value={formatAmountWithDecimals(
                          getAmountWithIva(values.amount, ivaParameter),
                          2
                        )}
                        variant="outlined"
                        disabled
                      />
                    </FieldLabel>
                  </Grid>
                </Grid>

                {values.category === TransactionsCategory.RENT &&
                  selectedContract &&
                  nextRent &&
                  rentError && (
                    <Typography
                      variant="body2"
                      color="secondary.main"
                      style={{ marginTop: '8px' }}
                    >
                      {t('SCHEMA_ERRORS.RENT_LIMIT')}
                    </Typography>
                  )}

                <FieldLabel
                  label={t('COMMON.INVOICE_CONCEPT')}
                  disabled={!values.contract}
                >
                  <TextField
                    InputProps={{
                      autoComplete: 'off',
                      endAdornment: isFetchingConcept ? (
                        <CircularProgress size={16} />
                      ) : null
                    }}
                    sx={invalidFieldSx}
                    error={Boolean(touched.comment && errors.comment)}
                    fullWidth
                    helperText={touched.comment && t(errors.comment)}
                    margin="none"
                    name="comment"
                    onBlur={handleBlur}
                    onChange={e => {
                      setConceptTouchedByUser(true);
                      handleChange(e);
                    }}
                    type="text"
                    value={values.comment}
                    variant="outlined"
                    disabled={!values.contract || isFetchingConcept}
                  />
                </FieldLabel>

                {values.category && (
                  <FieldLabel label={t('CFDI_FORM.PAYMENT_METHOD')}>
                  <FormControl fullWidth>
                    <DropdownMenu
                      list={getPaymentMethod(t)}
                      value={values.paymentMethod}
                      name="paymentMethod"
                      setValue={value => {
                        setFieldValue('paymentMethod', value);
                        if (value !== PaymentMethod.PUE) {
                          setFieldValue('paymentForm', '');
                        }
                      }}
                      error={Boolean(
                        touched.paymentMethod && errors.paymentMethod
                      )}
                      errorText={
                        touched.paymentMethod && t(errors.paymentMethod)
                      }
                      sx={invalidFieldSx}
                    />
                  </FormControl>
                  </FieldLabel>
                )}

                {values.paymentMethod === PaymentMethod.PUE && (
                  <FieldLabel label={t('CFDI_FORM.PAYMENT_FORM')}>
                  <FormControl fullWidth>
                    <DropdownMenu
                      list={PaymentFormList}
                      value={values.paymentForm}
                      name="paymentForm"
                      setValue={value => setFieldValue('paymentForm', value)}
                      error={Boolean(
                        touched.paymentForm && errors.paymentForm
                      )}
                      errorText={touched.paymentForm && t(errors.paymentForm)}
                      sx={invalidFieldSx}
                    />
                  </FormControl>
                  </FieldLabel>
                )}

                {values.category === TransactionsCategory.UPFRONT_RENT && (
                  <FieldLabel label="¿Desea continuar facturación del próximo mes?">
                    <DropdownMenu
                      list={[
                        { key: 'true', name: t('COMMON.YES') },
                        { key: 'false', name: t('COMMON.NO') }
                      ]}
                      value={values.continueBilling}
                      setValue={value =>
                        setFieldValue('continueBilling', value === 'true')
                      }
                      error={Boolean(
                        touched.continueBilling && errors.continueBilling
                      )}
                      errorText={
                        touched.continueBilling && t(errors.continueBilling)
                      }
                      sx={invalidFieldSx}
                    />
                  </FieldLabel>
                )}

                <Box sx={{ mt: 2.5 }}>
                  <FormControlLabel
                    key="do-not-bill-transaction"
                    control={
                      <Checkbox
                        name="doNotBillTransaction"
                        color="primary"
                        checked={values.doNotBillTransaction}
                        onChange={() => {
                          setFieldValue(
                            'doNotBillTransaction',
                            !values.doNotBillTransaction
                          );
                        }}
                      />
                    }
                    label={t('TRANSACTION.DO_NOT_BILL_TRANSACTION')}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'text.secondary',
                      ml: 4,
                      mt: -0.5
                    }}
                  >
                    {t('TRANSACTION.DO_NOT_BILL_CHARGE_HELP')}
                  </Typography>
                </Box>

                <Divider sx={{ mt: 3, mb: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1
                  }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      resetForm(initialState);
                      handleClose();
                    }}
                    disabled={loading}
                  >
                    {t('COMMON.CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {loading ? t('COMMON.REGISTERING') : t('COMMON.REGISTER')}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

GenerateManualChargeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  clientId: PropTypes.number
};

export default GenerateManualChargeDialog;
